// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Calendar from "@flip/XDL/Icons/Calendar";
import Close from "@flip/XDL/Icons/Close";
import PaginationLeft from "@flip/XDL/Icons/PaginationLeft";
import PaginationRight from "@flip/XDL/Icons/PaginationRight";
import { xBaseTheme } from "@flip/XDL/types";
import { color } from "@flip/XDL/Utilities";
import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...DatePickerPresentationPropsDefault,
        mergeCss: true,
        dateTimePickerProps: {
            showNeighboringMonth: true,
            nextLabel: React.createElement(PaginationRight),
            prevLabel: React.createElement(PaginationLeft),
        } as any,
        clearIconProps: {
            color: "common.ui4",
            src: Close,
            size: 10,
            css: css`
                &&& {
                    margin-right: 8px;
                    padding: 0;
                }
                &:hover {
                    color: ${color("common.ui4")};
                }
            `,
        },
        calendarIconProps: {
            color: "primary",
            src: Calendar,
            size: 16,
            css: css`
                &&& {
                    padding: 0;
                }
                &:hover {
                    color: ${color("text.link")};
                }
            `,
        },
        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YYYY",
            hourPlaceholder: "hr",
            minutePlaceholder: "min",
        },
        cssOverrides: {
            formInputWrapper: css`
                button:focus {
                    /* outline is added on span for border reasons */
                    outline: none;
                    span {
                        outline: none;
                    }
                }

                &&& .react-datetime-picker {
                    :not(:disabled) {
                        .react-datetime-picker__button:hover > ${IconWrapper} {
                            color: ${color("common.ui8")};
                        }
                        .react-datetime-picker__clear-button:hover > ${IconWrapper} {
                            color: ${color("common.ui8")};
                        }
                        .react-datetime-picker__button:focus > ${IconWrapper} {
                            color: ${color("common.ui9")};
                            outline: none;
                        }
                        .react-datetime-picker__clear-button:focus > ${IconWrapper} {
                            color: ${color("common.ui9")};
                            outline: none;
                        }
                    }

                    &:not(:disabled),
                    &:focus-within {
                        outline: none;

                        .react-datetime-picker__button {
                            padding-left: 0;
                            padding-right: 0;
                            outline: none;
                        }

                        .react-datetime-picker__clear-button {
                            margin-left: 20px;
                            padding-right: 0;
                            outline: none;
                        }
                    }
                }
            `,
            datePicker: css`
                .react-calendar {
                    width: 280px;
                    box-shadow: none;
                    margin-top: 0;
                    outline: ${getColor("common.border")} solid 2px;
                    outline-offset: -1px;
                    padding: 15px 20px 20px 20px;
                    button {
                        font-family: inherit;
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${getColor("common.white")};
                                background-color: ${getColor("common.ui4")};
                            }
                        }
                    }

                    .react-calendar__navigation {
                        margin-bottom: 15px;

                        &__arrow {
                            height: auto;
                        }

                        &__prev-button,
                        &__next-button {
                            min-width: 0;
                            padding: 0;
                            width: auto;
                            color: ${getColor("primary")};
                            background-image: none;
                            background: transparent;
                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("primary")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui8")};
                                }
                            }

                            svg {
                                width: 8px;
                                height: 12px;
                            }
                        }

                        &__label {
                            font-size: 16px;
                            font-weight: 600;
                            line-height: 18px;

                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("text")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui8")};
                                }
                            }
                        }
                    }

                    &__month-view {
                        &__weekdays {
                            font-size: 10px;
                            font-weight: normal;
                            color: ${getColor("text")};
                            letter-spacing: 0.4px;
                            text-transform: uppercase;
                            margin-bottom: 5px;
                        }

                        &__days {
                            &__day {
                                color: ${getColor("text")};
                                border-radius: 0;
                                font-weight: normal;
                                font-size: 12px;
                                height: 29px;
                                width: 29px;
                                padding: 0;

                                &.react-calendar__tile--now {
                                    position: relative;
                                    &:after {
                                        content: "";
                                        position: absolute;
                                        bottom: 3px;
                                        left: 50%;
                                        transform: translateX(-50%);
                                        color: ${getColor("primary")};
                                        border: 2px solid ${getColor("primary")};
                                        border-radius: 50%;
                                        font-size: 0;
                                    }
                                }

                                &:disabled,
                                &--neighboringMonth {
                                    color: ${getColor("common.border")};
                                }
                            }
                        }
                    }

                    &__year-view,
                    &__decade-view,
                    &__century-view {
                        .react-calendar__tile {
                            color: ${getColor("secondary")};
                            padding: 5px 0;
                            font-weight: normal;
                            font-size: 12px;

                            &:disabled {
                                color: ${getColor("common.border")};
                            }
                        }
                    }

                    &__tile {
                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            &:after {
                                content: none;
                            }
                        }
                    }
                }
            `,
        },
    },
} as xBaseTheme["datePicker"];
